import { z } from "zod";

// ── Bank transaction status labels ───────────────────────────────────────────
// Display mapping for the review-queue status. The canonical value lives on
// the `bank_transaction.status` column (pending | approved | skipped).

export const BANK_TRANSACTION_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  approved: "Approved",
  skipped: "Skipped",
};

export function bankTransactionStatusLabel(
  status: string | null | undefined,
): string {
  if (!status) return "—";
  return BANK_TRANSACTION_STATUS_LABELS[status] ?? status;
}

// ── Line schema ──────────────────────────────────────────────────────────────
// Multi-line journal-entry-style entries staged on a bank transaction. When
// the transaction is approved these lines are copied onto the hard-linked
// journal entry (source = "bank").

export const BankTransactionLineSchema = z.object({
  id: z.string(),
  bankTransactionId: z.string(),
  accountId: z.string().trim().min(1),
  debit: z.number().nullable(),
  credit: z.number().nullable(),
  description: z.string().optional(),
});

export type BankTransactionLine = z.infer<typeof BankTransactionLineSchema>;

export const CreateBankTransactionLineSchema = z.object({
  accountId: z.string().trim().min(1),
  debit: z.number().nonnegative().nullable(),
  credit: z.number().nonnegative().nullable(),
  description: z.string().optional(),
});

export type CreateBankTransactionLineInput = z.infer<
  typeof CreateBankTransactionLineSchema
>;

// ── Domain schema (DB / server truth) ─────────────────────────────────────────
// Mirrors the `bank_transaction` table. `institutionName` / `bankAccountName` /
// `bankAccountMask` are joined from the linked `bank_connection` and
// `bank_connection_account` rows for display; `mappedAccountCode` /
// `mappedAccountName` are joined from the linked `chart_account` row.

export const BankTransactionSchema = z.object({
  id: z.string(),
  bankConnectionId: z.string().nullable(),
  bankConnectionAccountId: z.string(),
  businessId: z.string(),
  provider: z.string(),
  providerTransactionId: z.string(),
  date: z.union([z.string(), z.date()]),
  name: z.string(),
  merchantName: z.string().optional(),
  amount: z.number(),
  currencyCode: z.string(),
  category: z.string().optional(),
  providerCategoryId: z.string().optional(),
  pending: z.boolean(),
  status: z.enum(["pending", "approved", "skipped"]),
  mappedAccountId: z.string().optional(),
  // Joined display fields — null/undefined when not mapped.
  mappedAccountCode: z.string().optional(),
  mappedAccountName: z.string().optional(),
  reviewedAt: z.union([z.string(), z.date()]).optional(),
  postedJournalEntryId: z.string().optional(),
  // Multi-line entries staged on the transaction (empty until lines are added).
  lines: z.array(BankTransactionLineSchema).default([]),
  // Connection details — joined for display.
  institutionName: z.string().optional(),
  bankAccountName: z.string().optional(),
  bankAccountMask: z.string().optional(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});

export type BankTransaction = z.infer<typeof BankTransactionSchema>;

export const CreateBankTransactionSchema = BankTransactionSchema.omit({
  id: true,
  businessId: true,
  mappedAccountCode: true,
  mappedAccountName: true,
  institutionName: true,
  bankAccountName: true,
  bankAccountMask: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  // Lines are created without id/bankTransactionId (assigned server-side).
  lines: z.array(CreateBankTransactionLineSchema).default([]),
});

export const UpdateBankTransactionSchema =
  CreateBankTransactionSchema.partial();

export type CreateBankTransactionInput = z.infer<
  typeof CreateBankTransactionSchema
>;
export type UpdateBankTransactionInput = z.infer<
  typeof UpdateBankTransactionSchema
>;

// ── Form schema (UI layer) ────────────────────────────────────────────────────

export const BankTransactionLineFormSchema = z.object({
  /** Stable client-side key used to keep rows stable across re-renders. */
  key: z.string().optional(),
  accountId: z.string(),
  debit: z.string(),
  credit: z.string(),
  description: z.string().optional(),
});

export type BankTransactionLineForm = z.infer<
  typeof BankTransactionLineFormSchema
>;

/**
 * Build the bank transaction form schema. When `amount` is provided, the lines
 * are additionally validated to balance to the absolute transaction amount —
 * so the user can't approve a transaction whose journal entry lines don't add
 * up to the actual bank movement.
 */
export function createBankTransactionFormSchema(amount?: number) {
  return z
    .object({
      name: z.string().min(1, "Name is required"),
      merchantName: z.string().optional(),
      category: z.string().optional(),
      status: z.enum(["pending", "approved", "skipped"]),
      lines: z.array(BankTransactionLineFormSchema),
    })
    .superRefine((val, ctx) => {
      // Only validate balance when at least one line has an account selected.
      const filled = val.lines.filter((e) => e.accountId);
      if (filled.length === 0) return;
      const debits = filled.reduce(
        (s, e) => s + (parseAmount(e.debit) ?? 0),
        0,
      );
      const credits = filled.reduce(
        (s, e) => s + (parseAmount(e.credit) ?? 0),
        0,
      );
      if (Math.abs(debits - credits) > 0.0001) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Lines are not balanced: debits ${debits.toFixed(4)} ≠ credits ${credits.toFixed(4)}.`,
          path: ["lines"],
        });
        return;
      }
      // When approving, the lines must also balance to the transaction amount.
      if (amount != null && val.status === "approved") {
        const absAmount = Math.abs(amount);
        if (Math.abs(debits - absAmount) > 0.0001) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Journal entry lines total ${debits.toFixed(4)} but the transaction amount is ${absAmount.toFixed(4)}. Adjust the lines so they balance to the transaction amount before approving.`,
            path: ["lines"],
          });
        }
      }
    });
}

export const BankTransactionFormSchema = createBankTransactionFormSchema();

export type BankTransactionForm = z.infer<typeof BankTransactionFormSchema>;

// ── Mapping: Form → Domain ────────────────────────────────────────────────────

/** Convert a user-entered amount string to a number, stripping locale-specific
 *  formatting like thousands separators (commas) so "1,620" → 1620. Returns
 *  `null` for empty strings, `NaN` for truly unparseable input. */
export function parseAmount(value: string): number | null {
  if (value === "") return null;
  const cleaned = value.replace(/,/g, "");
  return Number(cleaned);
}

export function formToDomain(
  input: BankTransactionForm,
): UpdateBankTransactionInput {
  return {
    name: input.name,
    merchantName: input.merchantName || undefined,
    category: input.category || undefined,
    status: input.status,
    lines: input.lines
      .filter((e) => e.accountId)
      .map((e) => ({
        accountId: e.accountId,
        debit: e.debit !== "" ? parseAmount(e.debit) : null,
        credit: e.credit !== "" ? parseAmount(e.credit) : null,
        description: e.description,
      })),
  };
}

export function domainToForm(item: BankTransaction): BankTransactionForm {
  return {
    name: item.name,
    merchantName: item.merchantName ?? "",
    category: item.category ?? "",
    status: item.status,
    lines: (item.lines ?? []).map((e) => ({
      key: `line-${e.id}`,
      accountId: e.accountId,
      debit: e.debit != null ? String(e.debit) : "",
      credit: e.credit != null ? String(e.credit) : "",
      description: e.description,
    })),
  };
}
